import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Twitter, 
  Share2, 
  BadgeCheck, 
  Sparkles,
  ChevronRight,
  HelpCircle,
  BookOpen,
  Bell,
  ArrowRight,
  DollarSign
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { BackgroundAnimation } from '@/components/background-animation';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { BLOG_POSTS } from '@/data/blog-posts';

const renderTextWithLinks = (text: string) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const linkText = match[1];
    const linkUrl = match[2];

    if (linkUrl.startsWith('mailto:') || linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
      parts.push(
        <a 
          key={match.index} 
          href={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer"
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(
        <Link 
          key={match.index} 
          to={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer"
        >
          {linkText}
        </Link>
      );
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center space-y-6">
          <h1 className="text-4xl font-bold font-display uppercase italic">Protocol Missing</h1>
          <p className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">The requested log could not be located.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-black font-black uppercase tracking-widest text-[10px]">
            <ArrowLeft className="w-4 h-4" /> Return to Directory
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = BLOG_POSTS
    .filter(p => p.slug !== slug && p.category === post.category)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground font-sans pt-24 pb-24 overflow-x-hidden relative">
      <div className="absolute inset-0 noise-bg opacity-[0.02] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-[800px] bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.08)_0%,transparent_70%)]" />
        <div className="absolute bottom-0 left-0 w-full h-[600px] bg-[radial-gradient(circle_at_20%_100%,rgba(6,182,212,0.05)_0%,transparent_60%)]" />
      </div>
      <SEO 
        title={post.title}
        description={post.description}
        keywords={`${post.category}, physics simulation, asmr art`}
      />
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div 
           initial={{ opacity: 0, y: 10 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           className="mb-12 space-y-8 mt-6"
        >
          <Link to="/blog" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Back to Journal
          </Link>

          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="font-sans text-[10px] font-bold uppercase tracking-widest px-3 py-1 border-cyan-600/10 bg-cyan-600/5 text-cyan-600 shadow-sm">
                {post.category}
              </Badge>
              <span className="text-[10px] font-bold text-zinc-400 uppercase flex items-center gap-2">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
            </div>

            <div className="relative">
              <h1 className="text-3xl md:text-4xl font-bold text-cyan-950 tracking-tighter leading-tight relative z-10 text-balance uppercase">
                {post.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center justify-between py-6 border-y border-cyan-50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-cyan-600/10 flex items-center justify-center text-[10px] font-bold text-cyan-600 border border-cyan-100">
                FC
              </div>
              <div className="flex flex-col">
                 <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-cyan-950 uppercase tracking-tight">{post.author.name}</span>
                    <BadgeCheck className="w-3 h-3 text-cyan-500" />
                 </div>
                 <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">{post.date}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
               <button 
                onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
                }}
                className="p-4 rounded-2xl border border-cyan-100 bg-white flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-all shadow-sm"
               >
                  <Twitter className="w-5 h-5" />
               </button>
               <button 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: post.title, url: window.location.href });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="p-4 rounded-2xl border border-cyan-100 bg-white flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-all shadow-sm"
               >
                  <Share2 className="w-5 h-5" />
               </button>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-12"
        >
          {post.content.map((block, idx) => {
            switch (block.type) {
              case 'heading':
                return (
                  <h2 key={idx} className="text-2xl sm:text-3xl font-black tracking-tight mt-16 mb-6 text-cyan-950 uppercase">
                    {block.content}
                  </h2>
                );
              case 'text':
                return (
                  <p key={idx} className="text-zinc-600 text-lg leading-relaxed mb-8 font-medium">
                    {renderTextWithLinks(block.content as string)}
                  </p>
                );
              case 'highlight':
                return (
                  <div key={idx} className="bg-cyan-50/50 p-8 rounded-3xl border-l-4 border-cyan-600 my-10">
                    <p className="text-cyan-950 font-bold text-lg italic">
                      {renderTextWithLinks(block.content as string)}
                    </p>
                  </div>
                );
              case 'list':
                return (
                  <ul key={idx} className="space-y-4 my-10">
                    {(block.content as string[]).map((item, i) => (
                      <li key={i} className="flex items-start gap-4 text-zinc-600 text-lg leading-relaxed">
                        <span className="text-cyan-400">•</span>
                        {renderTextWithLinks(item)}
                      </li>
                    ))}
                  </ul>
                );
              case 'quote':
                return (
                  <blockquote key={idx} className="border-l-4 border-cyan-100 pl-8 italic text-xl font-medium text-cyan-900/50 my-16 py-8">
                    {renderTextWithLinks(block.content as string)}
                  </blockquote>
                );
              default:
                return null;
            }
          })}
        </motion.div>

        <div className="mt-24 flex flex-col md:flex-row items-center justify-between gap-10 py-8 border-b border-cyan-50">
           <div className="flex items-center gap-6">
              <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Tags:</span>
              <div className="flex flex-wrap gap-3">
                 {['Physics', 'ASMR', 'Viral', post.category].map(tag => (
                    <span key={tag} className="text-xs font-medium px-4 py-1.5 bg-cyan-50 border border-cyan-100 rounded-full text-cyan-600 shadow-sm transition-all hover:border-cyan-600 hover:bg-cyan-600 hover:text-white cursor-default uppercase tracking-widest">{tag}</span>
                 ))}
              </div>
           </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-24">
             <div className="flex items-center justify-between mb-12">
                <h3 className="text-2xl font-bold text-cyan-950 tracking-tight uppercase">You might also like</h3>
                <Link to="/blog" className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-cyan-600 transition-all">View all articles</Link>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {relatedPosts.map(related => (
                   <Link key={related.slug} to={`/blog/${related.slug}`} className="group p-6 rounded-3xl bg-white border border-cyan-600/5 hover:border-cyan-600/20 transition-all space-y-4 hover:shadow-xl hover:shadow-cyan-600/5">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                           <span className="text-cyan-600">{related.category}</span>
                           <span className="w-1 h-1 rounded-full bg-zinc-200" />
                           <span>{related.date}</span>
                        </div>
                        <h4 className="text-xl font-bold text-cyan-950 group-hover:text-cyan-600 transition-colors tracking-tight uppercase">
                           {related.title}
                        </h4>
                      </div>
                   </Link>
                ))}
             </div>
          </div>
        )}

        {/* Continue Learning & Cross Links */}
        <div className="mt-24 pt-12 border-t border-cyan-50 space-y-12">
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-cyan-950 uppercase tracking-tight">Continue Learning</h3>
            <p className="text-zinc-500 font-sans text-sm font-medium">
              Expand your knowledge and master the creator tools with our comprehensive resources.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Link to="/tutorials" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                <div className="space-y-4">
                  <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Creator Tutorials</h4>
                  <p className="text-xs text-zinc-500 font-medium">Step-by-step guides for producing viral animations.</p>
                </div>
                <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/help" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                <div className="space-y-4">
                  <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Help Center</h4>
                  <p className="text-xs text-zinc-500 font-medium">Detailed reference guides and troubleshooting documentation.</p>
                </div>
                <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Read Guides <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/updates" className="p-6 rounded-3xl bg-cyan-50/20 border border-cyan-600/5 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-600/5">
                <div className="space-y-4">
                  <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Bell className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Feature Updates</h4>
                  <p className="text-xs text-zinc-500 font-medium">Keep track of the latest upgrades to our physics engine.</p>
                </div>
                <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  View Logs <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-bold text-cyan-950 uppercase tracking-tight">Creator Software & Pricing</h3>
            <p className="text-zinc-500 font-sans text-sm font-medium">
              Ready to take your social channels to the next level? Explore our suite of satisfying video generators.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Link to="/plans" className="p-6 rounded-3xl bg-white border border-zinc-200 hover:border-cyan-600/30 hover:shadow-lg transition-all flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center border border-cyan-100 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Flexible Pricing Plans</h4>
                    <p className="text-xs text-zinc-400 font-medium">Find the right workspace level for your content scaling needs.</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/ball-simulation" className="p-6 rounded-3xl bg-white border border-zinc-200 hover:border-cyan-600/30 hover:shadow-lg transition-all flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center border border-cyan-100 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Simulation Engine</h4>
                    <p className="text-xs text-zinc-400 font-medium">Build, customize, and export interactive rhythmic physics.</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <DiscordCTA variant="banner" />
        </div>

        <div className="mt-12">
          <InternalLinksSection />
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
